import { useState } from "react"
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material"
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Comment as CommentIcon,
} from "@mui/icons-material"
import { useApiMutation } from "../../hooks/useApi"
import { useQueryClient } from "@tanstack/react-query"
import api from "../../utils/api"
import JobsTable from "../../components/jobs/JobsTable"
import JobDetailsDialog from "../../components/jobs/JobDetailsDialog"

function SearchJobsPage() {
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("All")
  const [hasSearched, setHasSearched] = useState(false)
  
  // Dialog states
  const [selectedJob, setSelectedJob] = useState(null)
  const [jobDetailsForView, setJobDetailsForView] = useState(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [addCommentDialogOpen, setAddCommentDialogOpen] = useState(false)
  const [comment, setComment] = useState("")

  const queryClient = useQueryClient()

  const {
    mutate: searchJobsMutation,
    data,
    isError,
    isPending,
    error
  } = useApiMutation(`/jobs/search`, "POST")

  const setJobCommentMutation = useApiMutation(
    `/jobs/${selectedJob?.JobId}/comments`, 
    "POST", 
    {
      onSuccess: () => {
        setAddCommentDialogOpen(false)
        setComment("")
        // Re-run search to refresh results
        handleSearch()
      }
    }
  )

  const searchResults = data?.transformedJobs || []

  const handleSearch = async () => {
    if (!searchQuery.trim() && statusFilter === "All") return

    setHasSearched(true)

    searchJobsMutation({
      searchTerm: searchQuery.trim(),
      status: statusFilter !== "All" ? statusFilter.toLowerCase() : undefined
    })
  }

  // Define table columns
  const columns = [
    { id: "jobId", label: "Job ID", field: "JobId", type: "jobId" },
    { 
      id: "client", 
      label: "Client", 
      render: (job) => job.clientId?.name || "N/A"
    },
    { 
      id: "jobType", 
      label: "Job Type", 
      render: (job) => job.jobTypeId?.name || "N/A"
    },
    { id: "dueDate", label: "Due Date", field: "dueDate", type: "date" },
    { id: "status", label: "Status", field: "status", type: "status" },
    { 
      id: "assignedTo", 
      label: "Assigned To", 
      render: (job) => (
        <Box>
          <Typography variant="body2">
            {job?.assignedTo?.name || "Unassigned"}
          </Typography>
          {job?.secondaryUser && job?.secondaryUser?.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              + {job.secondaryUser.map((u) => u.name).join(", ")}
            </Typography>
          )}
        </Box>
      ),
    },
    { id: "createdAt", label: "Created At", field: "createdAt", type: "date" },
  ]

  // Define actions
  const actions = [
    {
      id: "view",
      label: "View Details",
      icon: <VisibilityIcon />,
    },
    {
      id: "addComment",
      label: "Add Comment",
      icon: <CommentIcon />,
    },
  ]

  const handleActionClick = async (action, job) => {
    setSelectedJob(job)

    switch (action.id) {
      case "view":
        // Fetch full job details
        try {
          const jobDetails = await api.get(`/jobs/${job.JobId}`)
          setJobDetailsForView(jobDetails.data[0])
          setViewDialogOpen(true)
        } catch (error) {
          console.error("Failed to fetch job details:", error)
        }
        break
      case "addComment":
        setAddCommentDialogOpen(true)
        break
      default:
        break
    }
  }

  const handleAddCommentSave = async () => {
    try {
      if (!comment) return
      const data = { commentText: comment }
      await setJobCommentMutation.mutateAsync(data)
    } catch (error) {
      console.error('Failed to add comment', error)
    }
  }

  const formatStatus = (status) => {
    const statusMap = {
      new: "New",
      processing: "Processing",
      completed: "Completed",
      cancelled: "Cancelled",
      closed: "Closed"
    }
    return statusMap[status] || status
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Search Jobs
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Search for jobs by ID, client name, job type, or status.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Enter job ID, client name, or job type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch()
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="All">All Status</MenuItem>
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isPending || (!searchQuery.trim() && statusFilter === "All")}
            sx={{ minWidth: 120 }}
          >
            {isPending ? "Searching..." : "Search"}
          </Button>
        </Box>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error searching jobs: {error?.response?.data?.message || error?.message || "Unknown error"}
        </Alert>
      )}

      {hasSearched && !isPending && (
        <Box>
          {searchResults.length === 0 ? (
            <Alert severity="info">No jobs found matching your search criteria.</Alert>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Search Results ({searchResults.length} found)
              </Typography>
              
              {/* Jobs Table - Reusable Component */}
              <JobsTable
                jobs={searchResults}
                columns={columns}
                actions={actions}
                onActionClick={handleActionClick}
                emptyMessage="No jobs found"
              />
            </Box>
          )}
        </Box>
      )}

      {/* View Details Dialog - Reusable Component */}
      <JobDetailsDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false)
          setJobDetailsForView(null)
          setSelectedJob(null)
        }}
        job={jobDetailsForView}
      />

      {/* Add Comment Dialog */}
      <Dialog 
        open={addCommentDialogOpen} 
        onClose={() => setAddCommentDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedJob.JobId} - {selectedJob.jobTypeId?.name || "N/A"}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Client: {selectedJob.clientId?.name || "N/A"}
              </Typography>
              <TextField
                fullWidth
                label="Comments"
                multiline
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add notes about the job..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCommentDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddCommentSave}
            variant="contained"
            disabled={!comment || setJobCommentMutation.isPending}
          >
            {setJobCommentMutation.isPending ? "Adding..." : "Add Comment"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SearchJobsPage